// Workstream G2 — unverified-claim inline marker machinery (string-domain half).
//
// The additive `unverified_claims` event (workstream A, flag-gated default OFF)
// carries answer spans [char_start, char_end) whose claims the P-Cite entailment
// gate could NOT bind to any source. This mirrors the ADR-099 citation
// char-offset span mechanism (attributionSentinels + rehypeAttributionChips),
// but a claim is a RANGE, so we inject a PUA OPEN/CLOSE sentinel PAIR — distinct
// from the citation `\uE000` sentinel — computed in the SAME coordinate space as
// the citation sentinels so the two never shift each other's offsets.

import type { UnverifiedClaimRef } from "@/types";
import { codePointToUtf16 } from "./attributionOffsets";
import { computeInsertPos, detectCodeRegions, SENTINEL_CLOSE, SENTINEL_OPEN } from "./attributionSentinels";
import type { AttributionSpan } from "./rehypeAttributionChips";

export const U_OPEN = "\uE002";
export const U_OPEN_END = "\uE003";
export const U_CLOSE = "\uE004";
export const U_CLOSE_END = "\uE005";

export interface UnverifiedSpan {
  ord: number;
  utf16Start: number;
  utf16End: number;
  claim: string;
  facetId: string;
}

// Map each claim's backend code-point offsets to UTF-16 units (the render
// coordinate space). A null (out-of-range / not-yet-streamed) or degenerate
// (start >= end) span is dropped — it reappears once the answer text catches up.
export function buildUnverifiedSpans(content: string, claims: readonly UnverifiedClaimRef[]): UnverifiedSpan[] {
  const out: UnverifiedSpan[] = [];
  for (let ord = 0; ord < claims.length; ord++) {
    const c = claims[ord];
    const utf16Start = codePointToUtf16(content, c.char_start);
    const utf16End = codePointToUtf16(content, c.char_end);
    if (utf16Start === null || utf16End === null) continue;
    if (utf16Start >= utf16End) continue;
    out.push({ ord, utf16Start, utf16End, claim: c.claim, facetId: c.facet_id });
  }
  return out;
}

interface Insert {
  pos: number;
  seq: number;
  text: string;
}

const EMPHASIS = new Set(["*", "_", "~"]);
const INLINE_WS = new Set([" ", "\t"]);

// Inject citation AND unverified sentinels in ONE right-to-left pass over the
// ORIGINAL content. With no unverified spans this is byte-identical to
// insertCitationSentinels (parity-pinned in unverifiedMarks.test.ts) so the
// citation render path can never regress.
export function insertClaimSentinels(
  content: string,
  citationSpans: readonly AttributionSpan[],
  unverifiedSpans: readonly UnverifiedSpan[],
): string {
  if (citationSpans.length === 0 && unverifiedSpans.length === 0) return content;
  // Collision guard: corpus text never carries our PUA sentinels; if it somehow
  // did, injecting would corrupt the split — disable for this render.
  if (/[\uE000-\uE005]/.test(content)) return content;

  const codeRegions = detectCodeRegions(content);
  const inserts: Insert[] = [];

  // Citation point markers — seq = ord preserves insertCitationSentinels order.
  for (let ord = 0; ord < citationSpans.length; ord++) {
    const res = computeInsertPos(content, citationSpans[ord].utf16End, codeRegions);
    if (!res.drop) inserts.push({ pos: res.pos, seq: ord, text: `${SENTINEL_OPEN}${ord}${SENTINEL_CLOSE}` });
  }

  // Unverified range markers — both-or-neither so an open never runs away
  // unclosed. The large seq offset keeps them deterministically ordered at a
  // shared offset and never perturbs citation-only parity.
  for (const span of unverifiedSpans) {
    const s = computeInsertPos(content, span.utf16Start, codeRegions);
    const e = computeInsertPos(content, span.utf16End, codeRegions);
    if (s.drop || e.drop) continue;
    // Nudge the OPEN inward past leading inline-whitespace + emphasis delimiters
    // and the CLOSE inward past trailing emphasis delimiters, so a claim bounded
    // by **bold** / _em_ keeps each delimiter's flanking context (content/
    // whitespace on the OUTSIDE) — else a PUA sentinel wedged between `)` and `**`
    // makes the run non-flanking (CommonMark) and the delimiter leaks as literal.
    let sPos = s.pos;
    while (sPos < e.pos && (INLINE_WS.has(content[sPos]) || EMPHASIS.has(content[sPos]))) sPos++;
    let ePos = e.pos;
    while (ePos > sPos && EMPHASIS.has(content[ePos - 1])) ePos--;
    if (sPos >= ePos) continue;
    inserts.push({ pos: sPos, seq: 1_000_000 + span.ord * 2, text: `${U_OPEN}${span.ord}${U_OPEN_END}` });
    inserts.push({ pos: ePos, seq: 1_000_000 + span.ord * 2 + 1, text: `${U_CLOSE}${span.ord}${U_CLOSE_END}` });
  }

  inserts.sort((a, b) => b.pos - a.pos || b.seq - a.seq);

  let outStr = content;
  for (const ins of inserts) {
    outStr = outStr.slice(0, ins.pos) + ins.text + outStr.slice(ins.pos);
  }
  return outStr;
}
